import { useState } from 'react';
import { Card, Button, Form, Stack, Badge, Alert, Row, Col } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { exceptionsService, ApiError } from '@/services/api';
import { useAsync } from '@/common/hooks/use-async';
import { AsyncBoundary } from '@/common/components/async-boundary';
import { useAuth } from '@/common/hooks/use-auth';
import { FeedbackControl } from '@/common/components/feedback-control';

/** Days until (or since) an epoch-seconds expiry, for the SUBMITTED 90-day window. */
function expiryText(expiresAt?: number): string {
  if (!expiresAt) return '-';
  const days = Math.round((expiresAt * 1000 - Date.now()) / 86_400_000);
  const when = new Date(expiresAt * 1000).toLocaleDateString();
  if (days < 0) return `${when} (expired ${-days}d ago)`;
  return `${when} (in ${days}d)`;
}

export default function ExceptionDetail() {
  const [params] = useSearchParams();
  const exceptionId = params.get('exceptionId') ?? '';
  const { actor } = useAuth();
  const { data, loading, error, reload } = useAsync(
    () => exceptionsService.getException(exceptionId),
    [exceptionId],
  );
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionOk, setActionOk] = useState<string | null>(null);

  async function run(label: string, fn: () => Promise<unknown>) {
    setBusy(true);
    setActionError(null);
    setActionOk(null);
    try {
      await fn();
      setActionOk(`${label} succeeded`);
      reload();
    } catch (e) {
      const msg = e instanceof ApiError ? `${e.status}: ${e.message}` : String(e);
      setActionError(`${label} failed — ${msg}`);
    } finally {
      setBusy(false);
    }
  }

  const state = data?.state;
  const canApprove = state === 'DRAFTED';
  const canReject = state === 'DRAFTED' || state === 'APPROVED';
  const canWithdraw = state === 'DRAFTED';

  return (
    <Card>
      <Card.Header>Exception {exceptionId.slice(0, 8)}</Card.Header>
      <Card.Body>
        <AsyncBoundary loading={loading} error={error}>
          {data && (
            <>
              <dl className="row">
                <dt className="col-sm-3">CVE</dt><dd className="col-sm-9">{data.cveId}</dd>
                <dt className="col-sm-3">Repo</dt><dd className="col-sm-9">{data.repo}</dd>
                <dt className="col-sm-3">State</dt><dd className="col-sm-9"><Badge bg="info">{data.state}</Badge></dd>
                <dt className="col-sm-3">PR</dt>
                <dd className="col-sm-9">
                  {data.prUrl ? <a href={data.prUrl} target="_blank" rel="noreferrer">{data.prUrl}</a> : '-'}
                  {data.prState ? ` (${data.prState})` : ''}
                </dd>
                <dt className="col-sm-3">Component</dt><dd className="col-sm-9">{data.component ?? '-'}</dd>
                <dt className="col-sm-3">REQ#</dt>
                <dd className="col-sm-9">
                  {data.serviceNowReqNumber ?? '-'}
                  {data.externalExisting ? <Badge bg="secondary" className="ms-2">pre-existing</Badge> : null}
                </dd>
                {data.filedDate && (<><dt className="col-sm-3">Filed</dt><dd className="col-sm-9">{data.filedDate}</dd></>)}
                {(data.state === 'SUBMITTED' || data.state === 'EXPIRED' || data.expiresAt) && (
                  <>
                    <dt className="col-sm-3">Expires</dt>
                    <dd className="col-sm-9">{expiryText(data.expiresAt)}</dd>
                  </>
                )}
                {data.approvedBy && (<><dt className="col-sm-3">Approved by</dt><dd className="col-sm-9">{data.approvedBy}</dd></>)}
                {data.rejectedReason && (<><dt className="col-sm-3">Rejected</dt><dd className="col-sm-9">{data.rejectedReason}</dd></>)}
              </dl>

              {data.state === 'SUBMITTED' && (
                <Alert variant="info" className="py-2 small">
                  Active exception — vuln-sentinel suppresses new PRs for this CVE until it expires,
                  then the CVE re-enters the pipeline for re-evaluation.
                </Alert>
              )}
              {data.state === 'EXPIRED' && (
                <Alert variant="warning" className="py-2 small">
                  Expired — suppression has lifted; a re-reported finding will produce a fresh PR.
                </Alert>
              )}

              {actionOk && <Alert variant="success">{actionOk}</Alert>}
              {actionError && <Alert variant="danger">{actionError}</Alert>}

              <Row className="g-2 align-items-end">
                <Col md={6}>
                  <Form.Label>Reject reason</Form.Label>
                  <Form.Control value={reason} onChange={(e) => setReason(e.target.value)} placeholder="required to reject" />
                </Col>
              </Row>
              <Stack direction="horizontal" gap={2} className="mt-3">
                <Button
                  variant="success"
                  disabled={busy || !canApprove}
                  onClick={() => run('Approve', () => exceptionsService.approveException(exceptionId, actor))}
                >
                  Approve
                </Button>
                <Button
                  variant="danger"
                  disabled={busy || !canReject || !reason.trim()}
                  onClick={() => run('Reject', () => exceptionsService.rejectException(exceptionId, actor, reason.trim()))}
                >
                  Reject
                </Button>
                <Button
                  variant="outline-dark"
                  disabled={busy || !canWithdraw}
                  onClick={() => run('Withdraw', () => exceptionsService.withdrawException(exceptionId, actor))}
                >
                  Withdraw
                </Button>
              </Stack>
              <p className="text-muted mt-2 mb-3">
                Submit to ServiceNow is not available yet (REQ phase). Approved exceptions wait here.
              </p>

              {data.prUrl && (
                <FeedbackControl
                  subjectType="EXCEPTION"
                  subjectId={data.prUrl}
                  repo={data.repo}
                  submit={(subjectId, repo, actor, outcome, comment) =>
                    exceptionsService.submitExceptionFeedback(subjectId, repo, actor, outcome, comment)}
                  listFeedback={(subjectId) => exceptionsService.listExceptionFeedback(subjectId)}
                />
              )}
            </>
          )}
        </AsyncBoundary>
      </Card.Body>
    </Card>
  );
}
